import { computed, ref } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import type { PageResult } from '../../types'
import type {
  ProviderCode,
  PushApp,
  PushChannelConfig,
  PushDevice,
  PushTemplate,
  PushTask,
  NotificationView,
  NotificationStats,
  PushDeliveryLog,
  PushSetting,
  ProviderStats,
  WechatChannelTemplate,
  WechatUserBinding,
  WechatSubscription,
  PushTab,
  PushChannelTestResult,
  PushTaskDetail,
  NotificationDetail
} from './types'
import { PROVIDER_CATALOG } from './types'

const EMPTY_NOTIFICATION_PAGE: PageResult<NotificationView> = {
  records: [],
  total: 0,
  pageNo: 1,
  pageSize: 100
}

export function usePushCenterState(options: {
  createApiClient: () => ApiClient
  onError: (msg: string) => void
  onNotice: (msg: string) => void
}) {
  const loading = ref(false)
  const appCode = ref('')
  const selectedProvider = ref<ProviderCode | ''>('')
  const taskIdFilter = ref('')
  const logPageNo = ref(1)
  const logPageSize = ref(10)
  const taskKeyword = ref('')
  const taskPageNo = ref(1)
  const taskPageSize = ref(10)
  const deviceKeyword = ref('')
  const devicePageNo = ref(1)
  const devicePageSize = ref(10)
  const templatePageNo = ref(1)
  const templatePageSize = ref(10)
  const templateKeyword = ref('')
  const templateEnabled = ref('')
  const notificationPageNo = ref(1)
  const notificationPageSize = ref(10)
  const notificationKeyword = ref('')
  const notificationCategory = ref('')
  const configPageNo = ref(1)
  const configPageSize = ref(10)
  const configTotal = ref(0)
  const userIdFilter = ref('')
  const wechatUserIdFilter = ref('')
  const settingPageNo = ref(1)
  const settingPageSize = ref(10)
  const settingTotal = ref(0)
  const wechatKeyword = ref('')
  const wechatTemplatePageNo = ref(1)
  const wechatTemplatePageSize = ref(10)
  const wechatBindingPageNo = ref(1)
  const wechatBindingPageSize = ref(10)
  const wechatBindingTotal = ref(0)
  const wechatSubscriptionPageNo = ref(1)
  const wechatSubscriptionPageSize = ref(10)
  const wechatSubscriptionTotal = ref(0)

  const apps = ref<PushApp[]>([])
  const configs = ref<PushChannelConfig[]>([])
  const templates = ref<PushTemplate[]>([])
  const templateTotal = ref(0)
  const devices = ref<PushDevice[]>([])
  const deviceTotal = ref(0)
  const notifications = ref<NotificationView[]>([])
  const notificationTotal = ref(0)
  const notificationStats = ref<NotificationStats | null>(null)
  const tasks = ref<PushTask[]>([])
  const taskTotal = ref(0)
  const logs = ref<PushDeliveryLog[]>([])
  const logTotal = ref(0)
  const settings = ref<PushSetting[]>([])
  const providerStats = ref<ProviderStats[]>([])
  const wechatChannelTemplates = ref<WechatChannelTemplate[]>([])
  const wechatBindings = ref<WechatUserBinding[]>([])
  const wechatSubscriptions = ref<WechatSubscription[]>([])
  const wechatTemplateTotal = ref(0)
  let requestSequence = 0

  const selectedAppName = computed(() => {
    if (!appCode.value) return '全部应用'
    return apps.value.find((app) => app.appCode === appCode.value)?.appName ?? appCode.value
  })

  const configuredProviderCodes = computed(
    () => new Set(configs.value.map((config) => config.provider))
  )
  const activeTemplates = computed(() => templates.value.filter((template) => template.enabled))

  const providerSummary = computed(() => {
    const total = providerStats.value.reduce((sum, item) => sum + item.total, 0)
    const failed = providerStats.value.reduce((sum, item) => sum + item.failed, 0)
    const sent = providerStats.value.reduce((sum, item) => sum + item.sent, 0)
    const acked = providerStats.value.reduce((sum, item) => sum + item.acked, 0)
    const rate = total === 0 ? 0 : (sent / total) * 100
    return { total, sent, failed, acked, rate }
  })

  function withQuery(url: string, params: Record<string, string | number | boolean | undefined>) {
    const search = new URLSearchParams()
    for (const [key, val] of Object.entries(params)) {
      if (val !== undefined && val !== '') {
        search.set(key, String(val))
      }
    }
    const queryString = search.toString()
    return queryString ? `${url}?${queryString}` : url
  }

  async function loadOr<T>(promise: Promise<T>, fallback: T): Promise<T> {
    void fallback
    return promise
  }

  function isCurrentRequest(sequence: number | undefined) {
    return sequence === undefined || sequence === requestSequence
  }

  async function fetchApps(sequence?: number) {
    try {
      const api = options.createApiClient()
      const list = await loadOr(api.get<PushApp[]>('/api/saas/push/apps'), [])
      if (!isCurrentRequest(sequence)) return
      apps.value = Array.isArray(list) ? list : []
    } catch (err) {
      if (!isCurrentRequest(sequence)) return
      options.onError(err instanceof Error ? err.message : '应用列表加载失败')
    }
  }

  async function fetchConfigs(sequence?: number) {
    const api = options.createApiClient()
    const query = withQuery('/api/saas/push/channel-configs/page', {
      appCode: appCode.value || undefined,
      provider: selectedProvider.value || undefined,
      pageNo: configPageNo.value,
      pageSize: configPageSize.value
    })
    const page = await loadOr(api.get<PageResult<PushChannelConfig>>(query), {
      records: [],
      total: 0,
      pageNo: 1,
      pageSize: 10
    })
    if (!isCurrentRequest(sequence)) return
    configs.value = page.records
    configTotal.value = page.total
  }

  async function fetchTemplates(sequence?: number) {
    const api = options.createApiClient()
    const query = withQuery('/api/saas/push/templates', {
      appCode: appCode.value || undefined
    })
    const list = await loadOr(api.get<PushTemplate[]>(query), [])
    if (!isCurrentRequest(sequence)) return
    templates.value = Array.isArray(list) ? list : []
  }

  async function fetchTemplatePage(sequence?: number) {
    const api = options.createApiClient()
    const enabled = templateEnabled.value === '' ? undefined : templateEnabled.value === 'true'
    const page = await api.get<PageResult<PushTemplate>>(
      withQuery('/api/saas/push/templates/page', {
        appCode: appCode.value || undefined,
        keyword: templateKeyword.value || undefined,
        enabled,
        pageNo: templatePageNo.value,
        pageSize: templatePageSize.value
      })
    )
    if (!isCurrentRequest(sequence)) return
    templates.value = page.records
    templateTotal.value = page.total
  }

  async function fetchWechat(sequence?: number) {
    const api = options.createApiClient()
    const [tList, bList, sList] = await Promise.all([
      loadOr(
        api.get<PageResult<WechatChannelTemplate>>(
          withQuery('/api/saas/push/wechat/channel-templates/page', {
            appCode: appCode.value || undefined,
            keyword: wechatKeyword.value || undefined,
            pageNo: wechatTemplatePageNo.value,
            pageSize: wechatTemplatePageSize.value
          })
        ),
        {
          records: [],
          total: 0,
          pageNo: wechatTemplatePageNo.value,
          pageSize: wechatTemplatePageSize.value
        }
      ),
      loadOr(
        api.get<PageResult<WechatUserBinding>>(
          withQuery('/api/saas/push/wechat/bindings/page', {
            appCode: appCode.value || undefined,
            clientUserId: wechatUserIdFilter.value ? Number(wechatUserIdFilter.value) : undefined,
            keyword: wechatKeyword.value || undefined,
            pageNo: wechatBindingPageNo.value,
            pageSize: wechatBindingPageSize.value
          })
        ),
        {
          records: [],
          total: 0,
          pageNo: wechatBindingPageNo.value,
          pageSize: wechatBindingPageSize.value
        }
      ),
      loadOr(
        api.get<PageResult<WechatSubscription>>(
          withQuery('/api/saas/push/wechat/subscriptions/page', {
            appCode: appCode.value || undefined,
            clientUserId: wechatUserIdFilter.value ? Number(wechatUserIdFilter.value) : undefined,
            keyword: wechatKeyword.value || undefined,
            pageNo: wechatSubscriptionPageNo.value,
            pageSize: wechatSubscriptionPageSize.value
          })
        ),
        {
          records: [],
          total: 0,
          pageNo: wechatSubscriptionPageNo.value,
          pageSize: wechatSubscriptionPageSize.value
        }
      )
    ])
    if (!isCurrentRequest(sequence)) return
    wechatChannelTemplates.value = Array.isArray(tList?.records) ? tList.records : []
    wechatTemplateTotal.value = tList?.total ?? 0
    wechatBindings.value = Array.isArray(bList?.records) ? bList.records : []
    wechatSubscriptions.value = Array.isArray(sList?.records) ? sList.records : []
    wechatBindingTotal.value = bList?.total ?? 0
    wechatSubscriptionTotal.value = sList?.total ?? 0
  }

  async function fetchDevices(sequence?: number) {
    const api = options.createApiClient()
    const query = withQuery('/api/saas/push/devices', {
      appCode: appCode.value || undefined,
      keyword: deviceKeyword.value || undefined,
      pageNo: devicePageNo.value,
      pageSize: devicePageSize.value
    })
    const page = await api.get<PageResult<PushDevice>>(query)
    if (!isCurrentRequest(sequence)) return
    devices.value = page.records
    deviceTotal.value = page.total
  }

  async function fetchNotifications(sequence?: number) {
    const api = options.createApiClient()
    const [page, stats] = await Promise.all([
      loadOr(
        api.get<PageResult<NotificationView>>(
          withQuery('/api/saas/push/notifications', {
            appCode: appCode.value || undefined,
            keyword: notificationKeyword.value || undefined,
            category: notificationCategory.value || undefined,
            pageNo: notificationPageNo.value,
            pageSize: notificationPageSize.value
          })
        ),
        EMPTY_NOTIFICATION_PAGE
      ),
      loadOr(
        api.get<NotificationStats>(
          withQuery('/api/saas/push/notifications/stats', {
            appCode: appCode.value || undefined
          })
        ),
        null
      )
    ])
    if (!isCurrentRequest(sequence)) return
    notifications.value = page.records
    notificationTotal.value = page.total
    notificationStats.value = stats
  }

  async function fetchTasks(sequence?: number) {
    const api = options.createApiClient()
    const [taskPage, tmplList] = await Promise.all([
      loadOr(
        api.get<PageResult<PushTask>>(
          withQuery('/api/saas/push/tasks', {
            appCode: appCode.value || undefined,
            keyword: taskKeyword.value || undefined,
            pageNo: taskPageNo.value,
            pageSize: taskPageSize.value
          })
        ),
        { records: [], total: 0, pageNo: taskPageNo.value, pageSize: taskPageSize.value }
      ),
      loadOr(
        api.get<PushTemplate[]>(
          withQuery('/api/saas/push/templates', {
            appCode: appCode.value || undefined
          })
        ),
        []
      )
    ])
    if (!isCurrentRequest(sequence)) return
    tasks.value = taskPage.records
    taskTotal.value = taskPage.total
    templates.value = Array.isArray(tmplList) ? tmplList : []
  }

  async function fetchLogs(sequence?: number) {
    const api = options.createApiClient()
    const query = withQuery('/api/saas/push/logs', {
      taskId: taskIdFilter.value || undefined,
      pageNo: logPageNo.value,
      pageSize: logPageSize.value
    })
    const page = await api.get<PageResult<PushDeliveryLog>>(query)
    if (!isCurrentRequest(sequence)) return
    logs.value = page.records
    logTotal.value = page.total
  }

  async function fetchSettings(sequence?: number) {
    const api = options.createApiClient()
    const query = withQuery('/api/saas/push/settings/page', {
      appCode: appCode.value || undefined,
      userId: userIdFilter.value ? Number(userIdFilter.value) : undefined,
      pageNo: settingPageNo.value,
      pageSize: settingPageSize.value
    })
    const page = await loadOr(api.get<PageResult<PushSetting>>(query), {
      records: [],
      total: 0,
      pageNo: 1,
      pageSize: 10
    })
    if (!isCurrentRequest(sequence)) return
    settings.value = page.records
    settingTotal.value = page.total
  }

  async function fetchTab(tab: PushTab) {
    const sequence = ++requestSequence
    loading.value = true
    try {
      if (apps.value.length === 0) {
        await fetchApps(sequence)
      }
      switch (tab) {
        case 'configs':
          await fetchConfigs(sequence)
          break
        case 'templates':
          await fetchTemplatePage(sequence)
          break
        case 'wechat':
          await fetchWechat(sequence)
          break
        case 'devices':
          await fetchDevices(sequence)
          break
        case 'notifications':
          await fetchNotifications(sequence)
          break
        case 'tasks':
          await fetchTasks(sequence)
          break
        case 'logs':
          await fetchLogs(sequence)
          break
        case 'settings':
          await fetchSettings(sequence)
          break
      }
    } catch (err) {
      if (!isCurrentRequest(sequence)) return
      options.onError(err instanceof Error ? err.message : '数据加载失败')
    } finally {
      if (isCurrentRequest(sequence)) loading.value = false
    }
  }

  async function testChannel(payload: {
    appCode: string
    provider: ProviderCode
    environment?: string
    credential?: Record<string, unknown>
  }): Promise<PushChannelTestResult> {
    const api = options.createApiClient()
    return api.post<PushChannelTestResult>('/api/saas/push/channel-configs/test', payload)
  }

  async function toggleChannel(
    targetAppCode: string,
    provider: ProviderCode,
    enabled: boolean,
    environment = 'PROD'
  ): Promise<void> {
    const api = options.createApiClient()
    const url = withQuery('/api/saas/push/channel-configs/toggle', {
      appCode: targetAppCode,
      provider,
      environment,
      enabled
    })
    await api.post<void>(url)
    options.onNotice(enabled ? `通道已启用` : `通道已停用`)
    await fetchConfigs()
  }

  async function disableDevice(deviceId: number): Promise<void> {
    const api = options.createApiClient()
    await api.post<void>(`/api/saas/push/devices/${deviceId}/disable`)
    options.onNotice('设备已禁用')
  }

  async function cancelTask(taskId: number): Promise<void> {
    const api = options.createApiClient()
    await api.post<PushTask>(`/api/saas/push/tasks/${taskId}/cancel`)
    options.onNotice('任务已取消')
  }

  async function retryTask(taskId: number): Promise<void> {
    const api = options.createApiClient()
    await api.post<PushTask>(`/api/saas/push/tasks/${taskId}/retry`)
    options.onNotice('已创建重试任务')
  }

  async function revokeNotification(notificationId: number): Promise<void> {
    const api = options.createApiClient()
    await api.post(`/api/saas/push/notifications/${notificationId}/revoke`)
    options.onNotice('通知已撤销')
  }

  async function loadTaskDetail(taskId: number): Promise<PushTaskDetail> {
    const api = options.createApiClient()
    return api.get<PushTaskDetail>(`/api/saas/push/tasks/${taskId}`)
  }

  async function loadNotificationDetail(notificationId: number): Promise<NotificationDetail> {
    const api = options.createApiClient()
    return api.get<NotificationDetail>(`/api/saas/push/notifications/${notificationId}`)
  }

  return {
    loading,
    appCode,
    selectedProvider,
    taskIdFilter,
    logPageNo,
    logPageSize,
    taskKeyword,
    taskPageNo,
    taskPageSize,
    deviceKeyword,
    devicePageNo,
    devicePageSize,
    templatePageNo,
    templatePageSize,
    templateKeyword,
    templateEnabled,
    notificationPageNo,
    notificationPageSize,
    notificationKeyword,
    notificationCategory,
    userIdFilter,
    wechatUserIdFilter,
    apps,
    configs,
    templates,
    templateTotal,
    devices,
    deviceTotal,
    notifications,
    notificationTotal,
    notificationStats,
    tasks,
    taskTotal,
    logs,
    logTotal,
    settings,
    settingPageNo,
    settingPageSize,
    settingTotal,
    wechatKeyword,
    wechatTemplatePageNo,
    wechatTemplatePageSize,
    wechatBindingPageNo,
    wechatBindingPageSize,
    wechatBindingTotal,
    wechatSubscriptionPageNo,
    wechatSubscriptionPageSize,
    wechatSubscriptionTotal,
    providerStats,
    wechatChannelTemplates,
    wechatBindings,
    wechatSubscriptions,
    wechatTemplateTotal,
    selectedAppName,
    configuredProviderCodes,
    activeTemplates,
    providerSummary,
    fetchTab,
    fetchApps,
    fetchConfigs,
    configPageNo,
    configPageSize,
    configTotal,
    fetchTemplates,
    fetchTemplatePage,
    fetchWechat,
    fetchDevices,
    fetchNotifications,
    fetchTasks,
    fetchLogs,
    fetchSettings,
    testChannel,
    toggleChannel,
    disableDevice,
    cancelTask,
    retryTask,
    revokeNotification,
    loadTaskDetail,
    loadNotificationDetail
  }
}

export function providerLabel(provider: ProviderCode) {
  return PROVIDER_CATALOG[provider]?.label ?? provider
}

export function providerPlatform(provider: ProviderCode) {
  return PROVIDER_CATALOG[provider]?.platform ?? ''
}

export function providerAccent(provider: ProviderCode) {
  return PROVIDER_CATALOG[provider]?.accent ?? 'neutral'
}

export function formatTime(timeStr?: string | null) {
  if (!timeStr) return '-'
  try {
    const d = new Date(timeStr)
    return d.toLocaleString('zh-CN', { hour12: false })
  } catch {
    return timeStr
  }
}

export function statusText(enabled: boolean) {
  return enabled ? '已启用' : '已停用'
}
